import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, X, Eye } from "lucide-react";
import { toast } from "sonner";
import { PRODUCTS } from "../data/content";
import { Reveal, Tag, ArrowLink, EASE } from "../components/Shared";

const loadWishlist = () => {
  try { return JSON.parse(localStorage.getItem("rb-wishlist") || "[]"); } catch { return []; }
};

export const useWishlist = () => {
  const [wishlist, setWishlist] = useState(loadWishlist);
  const toggle = (id) => {
    setWishlist((w) => {
      const next = w.includes(id) ? w.filter((x) => x !== id) : [...w, id];
      localStorage.setItem("rb-wishlist", JSON.stringify(next));
      toast(w.includes(id) ? "Removed from wishlist" : "Added to wishlist");
      return next;
    });
  };
  return { wishlist, toggle };
};

export const ProductCard = ({ p, onQuickView, wishlist, onToggleWishlist }) => {
  const wished = wishlist.includes(p.id);
  return (
    <div className="group" data-testid={`product-card-${p.id}`}>
      <div className="relative aspect-[3/4] overflow-hidden bg-card">
        <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`}>
          <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-108" />
        </Link>
        <button
          type="button"
          onClick={() => onToggleWishlist(p.id)}
          data-testid={`wishlist-toggle-${p.id}`}
          aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
          className={`absolute right-4 top-4 border p-2.5 backdrop-blur-md transition-all duration-300 ${
            wished ? "border-flame bg-flame text-ink" : "border-white/20 bg-ink/40 text-bone hover:border-flame hover:text-flame"
          }`}
        >
          <Heart className="h-4 w-4" fill={wished ? "currentColor" : "none"} />
        </button>
        <button
          type="button"
          onClick={() => onQuickView(p)}
          data-testid={`quick-view-${p.id}`}
          className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-center gap-2 bg-ink/85 py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone backdrop-blur-md transition-transform duration-500 ease-out hover:text-flame group-hover:translate-y-0"
        >
          <Eye className="h-3.5 w-3.5" /> Quick View
        </button>
      </div>
      <div className="mt-5 flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-flame">{p.collection}</p>
          <h3 className="mt-1.5 font-serif text-xl font-normal">{p.name}</h3>
        </div>
        <p className="pt-4 font-mono text-xs text-bone/80" data-testid={`product-price-${p.id}`}>{p.price}</p>
      </div>
      <Link to={`/product/${p.id}`} data-testid={`view-piece-${p.id}`} className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-fog transition-colors duration-300 hover:text-flame">
        View Piece →
      </Link>
    </div>
  );
};

export const QuickViewModal = ({ product, onClose, wishlist, onToggleWishlist }) => (
  <AnimatePresence>
    {product && (
      <>
        <motion.div
          className="fixed inset-0 z-[95] bg-ink/80 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="quickview-backdrop"
        />
        <motion.div
          className="fixed left-1/2 top-1/2 z-[100] grid max-h-[90vh] w-[92vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto border border-line/10 bg-coal md:grid-cols-2"
          initial={{ opacity: 0, scale: 0.94, x: "-50%", y: "-50%" }}
          animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
          exit={{ opacity: 0, scale: 0.94, x: "-50%", y: "-50%" }}
          transition={{ duration: 0.45, ease: EASE }}
          data-testid="quickview-modal"
        >
          <div className="relative aspect-[3/4] md:aspect-auto">
            <img src={product.image} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col p-8">
            <button type="button" onClick={onClose} data-testid="quickview-close-button" aria-label="Close quick view" className="absolute right-4 top-4 border border-line/15 bg-ink/60 p-2 text-fog transition-colors hover:border-flame hover:text-flame">
              <X className="h-4 w-4" />
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame">{product.collection} · {product.sku}</p>
            <h3 className="mt-3 font-serif text-3xl font-light">{product.name}</h3>
            <p className="mt-2 font-mono text-sm text-bone">{product.price}</p>
            <p className="mt-5 text-sm font-light leading-relaxed text-fog">{product.description}</p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-fog">Fabric — <span className="text-bone/80">{product.fabric}</span></p>
            <div className="mt-6">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-fog">Sizes</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <span key={s} className="border border-line/15 px-3.5 py-2 font-mono text-[10px] tracking-widest text-bone/80">{s}</span>
                ))}
              </div>
            </div>
            <div className="mt-auto flex flex-col gap-3 pt-8">
              <button
                type="button"
                onClick={() => onToggleWishlist(product.id)}
                data-testid="quickview-wishlist-button"
                className="flex items-center justify-center gap-2 border border-flame py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-flame transition-all duration-300 hover:bg-flame hover:text-ink"
              >
                <Heart className="h-4 w-4" fill={wishlist.includes(product.id) ? "currentColor" : "none"} />
                {wishlist.includes(product.id) ? "Wishlisted" : "Add to Wishlist"}
              </button>
              <p className="text-center font-mono text-[9px] uppercase tracking-[0.25em] text-fog">Online checkout arrives with the full shop — enquire via consultation</p>
            </div>
          </div>
        </motion.div>
      </>
    )}
  </AnimatePresence>
);

const TheEdit = () => {
  const [quickView, setQuickView] = useState(null);
  const { wishlist, toggle } = useWishlist();

  useEffect(() => {
    document.body.style.overflow = quickView ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [quickView]);

  return (
    <section className="bg-coal px-5 py-28 sm:px-10 md:py-40" data-testid="the-edit-section">
      <div className="mx-auto max-w-[1600px]">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Reveal><Tag>Ready-to-Wear</Tag></Reveal>
            <h2 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl lg:text-7xl">
              THE EDIT — <span className="text-fog">READY WHEN YOU ARE.</span>
            </h2>
          </div>
          <Reveal><ArrowLink to="/shop" testId="explore-shop-link" big>Explore the Shop</ArrowLink></Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.08}>
              <ProductCard p={p} onQuickView={setQuickView} wishlist={wishlist} onToggleWishlist={toggle} />
            </Reveal>
          ))}
        </div>
      </div>
      <QuickViewModal product={quickView} onClose={() => setQuickView(null)} wishlist={wishlist} onToggleWishlist={toggle} />
    </section>
  );
};

export default TheEdit;
